import { Booking, SafariExtra } from '../types';

/**
 * WhatsApp Integration Service for Maasai SightTrails
 * Handles template generation and triggering wa.me links
 */

const ADMIN_WHATSAPP_NUMBER = '+254708560665'; // Updated admin number

export const whatsappService = {
    /**
     * Formats a professional booking summary for the guest
     */
    formatGuestMessage(booking: Booking, tourTitle: string): string {
        const extrasText = booking.selectedExtras?.length
            ? `\n*Extras Included:*\n${booking.selectedExtras.map(e => `• ${e.name}`).join('\n')}`
            : '';

        const payload = `
🦁 *SightTrails Safaris - Booking Selection Saved*

Hello *${booking.customerName}*, we've captured your safari details!

📋 *Your Expedition: ${tourTitle}*
📅 *Arrival Date:* ${booking.arrivalDate || 'Not specified'}
🌴 *Package:* ${booking.packageDetails?.tier || 'Standard'} Experience
👥 *Travelers:* ${typeof booking.participants === 'number' ? booking.participants : booking.participants.length}
${extrasText}

💰 *Price Breakdown:*
Total Amount: ${booking.currency} ${booking.totalPrice?.toLocaleString()}
Payment Plan: ${booking.paymentPlan || 'Full Payment'}

🚀 *Next Steps:*
To finalize your booking and secure your dates, please proceed with the payment on the checkout page.

We look forward to hosting you in the heart of the wild!
    `.trim();

        return encodeURIComponent(payload);
    },

    /**
     * Formats a detailed intelligence report for the admin
     */
    formatAdminAlert(booking: Booking, tourTitle: string): string {
        const extrasList = booking.selectedExtras?.length
            ? `\n*Extras:* ${booking.selectedExtras.map(e => e.name).join(', ')}`
            : '';

        const guestsList = Array.isArray(booking.participants)
            ? `\n*Guest Details:* \n${booking.participants.map((p, i) => `   ${i + 1}. ${p.firstName} (${p.category})`).join('\n')}`
            : '';

        const payload = `
🚨 *ADMIN ALERT: New Booking Intent*

A client has just proceeded to checkout for *${tourTitle}*.

👤 *Lead Guest:* ${booking.customerName}
📧 *Email:* ${booking.customerEmail}
📱 *Phone:* ${booking.customerPhone}
🌍 *Nationality:* ${booking.customerNationality}
📅 *Arrival:* ${booking.arrivalDate || 'TBD'}
${extrasList}${guestsList}

💵 *Financials:*
Total: ${booking.currency} ${booking.totalPrice?.toLocaleString()}
Plan: ${booking.paymentPlan}

Please reach out to the client for follow-up if payment is not completed within the hour.
    `.trim();

        return encodeURIComponent(payload);
    },

    /**
     * Opens WhatsApp Web or App with the pre-filled message
     */
    triggerWhatsApp(phone: string, encodedMessage: string) {
        // Sanitize phone number to digits only (except leading +)
        const cleanPhone = phone.replace(/[^\d+]/g, '').replace(/^\+/, '');
        const url = `https://wa.me/${cleanPhone}?text=${encodedMessage}`;
        window.open(url, '_blank');
    },

    /**
     * Orchestrates dual notifications
     */
    notifyCheckout(booking: Booking, tourTitle: string) {
        // 1. Notify Guest
        const guestMsg = this.formatGuestMessage(booking, tourTitle);
        this.triggerWhatsApp(booking.customerPhone || '', guestMsg);

        // 2. Notify Admin after short delay to ensure guest tab opens first
        setTimeout(() => {
            const adminMsg = this.formatAdminAlert(booking, tourTitle);
            this.triggerWhatsApp(ADMIN_WHATSAPP_NUMBER, adminMsg);
        }, 1500);
    },

    /**
     * Formats a verification message when admin approves booking/payment
     */
    formatVerificationMessage(booking: Booking, tourTitle: string): string {
        const payload = `
✅ *SightTrails Safaris - Booking Confirmed*

Hello *${booking.customerName}*, your expedition is now officially verified!

📋 *Your Expedition: ${tourTitle}*
📅 *Arrival Date:* ${booking.arrivalDate || 'Not specified'}

Your booking details and payments have been secured by our Admin team. 

Get ready for an unforgettable adventure! If you need any assistance, feel free to reply here.
    `.trim();
        return encodeURIComponent(payload);
    },

    /**
     * Triggered by Admin from Command Center upon verification
     */
    notifyVerification(booking: Booking, tourTitle: string) {
        const msg = this.formatVerificationMessage(booking, tourTitle);
        // Fallback to customer_phone if customerPhone is undefined on backend models
        const phone = booking.customerPhone || (booking as any).customer_phone || '';
        this.triggerWhatsApp(phone, msg);
    }
};
